import React, { useMemo } from 'react';
import { TaskItem, TaskList } from '../../../types/tasks';
import { useTasks } from '../../../context/TasksContext';
import { relativeDay, shortDate } from '../../../utils/taskDates';
import { isLate, rangeLabel } from '../../../utils/projectPlan';
import { isMaterial, materialTotal, money } from '../../../utils/projectValue';
import { Flag, comingUp, criticalPath, overdueAndLate, plannedFinish, waitingOn, whereLabel } from '../../../utils/projectDashboard';
import ProgressCheck from '../../tasks/ProgressCheck';
import { inDaysLabel, nextMarker, projectProgress } from '../projectStats';
import { DashCard, DashRow } from './DashCard';
import WorkloadCard from './WorkloadCard';

/**
 * A heads-up on projects: what's overdue or late, what's coming up in the
 * next two weeks, the critical path, what we're waiting on from suppliers,
 * and who has how much on. On the Projects page it covers every project
 * (`projects` has several); on a project's Overview tab, just that one.
 */
const ProjectDashboard: React.FC<{
  projects: TaskList[];
  items: TaskItem[];
  readOnly?: boolean;
  onOpenItem: (item: TaskItem) => void;
}> = ({ projects, items, readOnly, onOpenItem }) => {
  const { today, canEditList } = useTasks();
  const single = projects.length === 1 ? projects[0] : null;
  const projectOf = useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects]);
  const byId = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  // "Project › Branch" — the project is left out when there's only one.
  const where = (i: TaskItem, withProject = !single) =>
    whereLabel(i, byId, withProject ? projectOf.get(i.list_id)?.title || 'Untitled project' : undefined);
  const locked = (i: TaskItem) => readOnly || !canEditList(projectOf.get(i.list_id)!);

  const late = useMemo(() => overdueAndLate(items, today), [items, today]);
  const soon = useMemo(() => comingUp(items, today, 14), [items, today]);
  const critical = useMemo(() => criticalPath(items), [items]);
  const waiting = useMemo(() => waitingOn(items, today), [items, today]);
  const followUps = waiting.filter((w) => w.tone === 'warn').length;

  const row = (f: Flag, key: string) => (
    <DashRow key={key} item={f.item} flag={f} context={where(f.item)} readOnly={locked(f.item)} onOpen={onOpenItem} />
  );

  return (
    <div className="tp-dash">
      {single && <ProjectSummary project={single} items={items} />}

      <div className="tp-d-grid">
        <DashCard
          title="Overdue, late & conflicts"
          icon="critical"
          tone={late.some((f) => f.tone === 'danger') ? 'danger' : late.length ? 'warn' : 'normal'}
          count={late.length}
          empty="Nothing overdue, the plan makes every date, and no dates clash."
          rows={late.map((f) => row(f, `l${f.item.id}`))}
        />
        <DashCard
          title="Coming up · 2 weeks"
          icon="calendar"
          count={soon.length}
          empty="Nothing due, starting or happening in the next two weeks."
          rows={soon.map((f) => row(f, `s${f.item.id}-${f.label}`))}
        />
        <CriticalCard projects={projects} items={items} critical={critical} single={!!single} onOpenItem={onOpenItem} locked={locked} where={(i) => where(i, false)} />
        <DashCard
          title="Waiting on"
          icon="waiting"
          tone={followUps ? 'warn' : 'normal'}
          count={waiting.length}
          empty="Not waiting on anyone. Mark a task ⋯ › Waiting on… when it's with a supplier."
          rows={waiting.map((f) =>
            row({ ...f, label: f.tone === 'warn' ? `Follow up · ${f.label}` : f.label }, `w${f.item.id}`)
          )}
        />
        <WorkloadCard items={items} onOpenItem={onOpenItem} locked={locked} where={(i) => where(i)} />
      </div>
    </div>
  );
};

/** The critical path: one project's chain in order, or the next few on each project. */
const CriticalCard: React.FC<{
  projects: TaskList[];
  items: TaskItem[];
  critical: TaskItem[];
  single: boolean;
  onOpenItem: (item: TaskItem) => void;
  locked: (i: TaskItem) => boolean;
  /** The branch it's under (cards are already grouped by project). */
  where: (i: TaskItem) => string | undefined;
}> = ({ projects, items, critical, single, onOpenItem, locked, where }) => {
  const { today } = useTasks();
  const flagFor = (i: TaskItem) => ({
    label: isLate(i) ? `${-i.slack_days!}d late` : i.plan_start! <= today ? 'Under way' : rangeLabel(i.plan_start!, i.plan_finish!),
    tone: (isLate(i) ? 'danger' : i.plan_start! <= today ? 'warn' : 'muted') as 'danger' | 'warn' | 'muted',
  });

  if (single) {
    return (
      <DashCard
        title="Critical path"
        icon="gantt"
        tone={critical.some(isLate) ? 'danger' : 'normal'}
        count={critical.length}
        empty="No chain yet — link topics with Waits for… to see the longest chain."
        rows={critical.map((i) => <DashRow key={i.id} item={i} flag={flagFor(i)} context={where(i)} readOnly={locked(i)} onOpen={onOpenItem} />)}
        show={8}
      >
        {critical.length > 0 && <p className="tp-d-note">The longest chain of linked tasks.</p>}
      </DashCard>
    );
  }

  // Every project: the next three critical tasks on each, and when it finishes.
  const groups = projects
    .map((p) => ({ project: p, chain: critical.filter((i) => i.list_id === p.id), finish: plannedFinish(items.filter((i) => i.list_id === p.id)) }))
    .filter((g) => g.chain.length);
  return (
    <DashCard
      title="Critical path"
      icon="gantt"
      tone={critical.some(isLate) ? 'danger' : 'normal'}
      count={critical.length}
      empty="Nothing is critical on any project."
      rows={groups.map((g) => (
        <li key={g.project.id} className="tp-d-group">
          <div className="tp-d-group-head">
            <strong>{g.project.title || 'Untitled project'}</strong>
            {g.finish && <span className="tk-muted">finishes {shortDate(g.finish)}</span>}
          </div>
          <ul className="tp-d-list">
            {g.chain.slice(0, 3).map((i) => (
              <DashRow key={i.id} item={i} flag={flagFor(i)} context={where(i)} readOnly={locked(i)} onOpen={onOpenItem} />
            ))}
          </ul>
          {g.chain.length > 3 && <p className="tp-d-note">+{g.chain.length - 3} more on its critical path</p>}
        </li>
      ))}
      show={4}
    />
  );
};

/** The top of a project's Overview: progress, finish, next event, money left to spend, and each branch. */
const ProjectSummary: React.FC<{ project: TaskList; items: TaskItem[] }> = ({ project, items }) => {
  const { today } = useTasks();
  const progress = projectProgress(items);
  const finish = plannedFinish(items);
  const next = nextMarker(items, today);
  const toBuy = items.filter((i) => isMaterial(i) && i.progress < 100);
  const toBuyTotal = toBuy.reduce((t, m) => t + (materialTotal(m) ?? 0), 0);
  const branches = items.filter((i) => i.parent_id === null).sort((a, b) => a.sort_order - b.sort_order);

  return (
    <div className="tp-d-summary">
      <div className="tp-m-totals tp-d-stats">
        <span className="tp-m-total">
          <span>Done</span>
          <strong>{progress}%</strong>
          <small>{items.filter((i) => i.progress < 100).length} open</small>
        </span>
        <span className="tp-m-total">
          <span>Planned finish</span>
          <strong>{finish ? shortDate(finish) : '—'}</strong>
          <small>{finish ? relativeDay(finish, today) : 'Nothing scheduled'}</small>
        </span>
        <span className="tp-m-total is-event">
          <span>Next event</span>
          <strong>{next ? inDaysLabel(next.inDays) : '—'}</strong>
          <small>{next ? next.item.text : 'None coming up'}</small>
        </span>
        <span className="tp-m-total is-needed">
          <span>Still to buy</span>
          <strong>{money(toBuyTotal)}</strong>
          <small>{toBuy.length} item{toBuy.length === 1 ? '' : 's'}</small>
        </span>
      </div>

      {branches.length > 0 && (
        <ul className="tp-d-branches" aria-label={`Progress on ${project.title || 'the project'}`}>
          {branches.map((b) => (
            <li key={b.id} className={b.progress >= 100 ? 'is-done' : ''}>
              <ProgressCheck value={b.progress} label={b.text} size={16} disabled />
              <span className="tp-d-branch">{b.text || 'Topic'}</span>
              <span className="tl-progress-bar"><span style={{ width: `${b.progress}%` }} /></span>
              <span className="tl-progress-text">{b.progress}%</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ProjectDashboard;
